// frontend/source/portal/components/CreateCategoryCard.js
import React, { useState } from "react";
import ImagePicker from "./ImagePicker";
import ConfirmDialog from "./ConfirmDialog";
import "./CreateCategoryCard.css";

const CreateCategoryCard = ({ onCreate }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleStartCreate = () => {
    setIsCreating(true);
  };

  const handleCancel = () => {
    // Check if user has entered any data
    if (name.trim() !== "" || selectedImage) {
      setShowCancelConfirm(true);
    } else {
      resetForm();
    }
  };

  const confirmCancel = () => {
    resetForm();
    setShowCancelConfirm(false);
  };

  const resetForm = () => {
    setIsCreating(false);
    setName("");
    setSelectedImage(null);
  };

  const handleSave = async () => {
    if (name.trim() === "") {
      alert("Category name cannot be empty");
      return;
    }

    if (!selectedImage) {
      alert("Please select an image for the category");
      return;
    }

    try {
      setIsSaving(true);
      await onCreate({ name: name.trim(), image: selectedImage });
      // Reset form
      setName("");
      setSelectedImage(null);
      setIsCreating(false);
    } catch (err) {
      console.error("Failed to create category:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleImageSelect = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  if (!isCreating) {
    return (
      <div className="create-category-card" onClick={handleStartCreate}>
        <div className="create-category-placeholder">
          <div className="create-category-icon">+</div>
          <h2 className="create-category-text">Add New Category</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="create-category-card creating">
      <div className="create-category-card-image-container">
        {selectedImage ? (
          <>
            <img
              src={selectedImage}
              alt="Selected category"
              className="create-category-card-image"
            />
            <button
              onClick={() => setShowImagePicker(true)}
              className="create-category-change-image-btn"
              title="Change image"
              disabled={isSaving}
            >
              📷
            </button>
          </>
        ) : (
          <div
            className="create-category-image-placeholder"
            onClick={() => setShowImagePicker(true)}
          >
            <div className="create-category-image-icon">🖼️</div>
            <p>Click to select image</p>
          </div>
        )}
      </div>

      <div className="create-category-card-overlay">
        <div className="create-category-input-container">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyPress}
            className="create-category-input"
            placeholder="Category name"
            autoFocus
            disabled={isSaving}
          />
          <div className="create-category-buttons">
            <button
              onClick={handleSave}
              className="create-category-btn save-btn"
              disabled={isSaving || !name.trim() || !selectedImage}
            >
              {isSaving ? "Creating..." : "Create"}
            </button>
            <button
              onClick={handleCancel}
              className="create-category-btn cancel-btn"
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {showImagePicker && (
        <ImagePicker
          currentImage={selectedImage}
          onImageSelect={handleImageSelect}
          onClose={() => setShowImagePicker(false)}
        />
      )}

      <ConfirmDialog
        isOpen={showCancelConfirm}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to cancel creating this category?"
        confirmText="Discard"
        cancelText="Keep Editing"
        type="warning"
        onConfirm={confirmCancel}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
};

export default CreateCategoryCard;
